import { useEffect, useSyncExternalStore } from "react";
import { ORG_TEAM_SELECTION_CHANGED_EVENT } from "#app/contexts/org-team-context.tsx";
import { useSessionsStore, type ChatThread } from "#app/store/sessions";
import {
  getChats,
  getWorkflowServiceContextHeaders,
} from "#app/utils/workflow-svc";

const LIMIT = 20;

type PageSnapshot = {
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  page: number;
  chats: ChatThread[];
};

/**
 * The desktop sidebar and the tablet rail each mount this hook. Separate
 * page state made them fetch the same pages and overwrite the list, so the
 * search dialog's "Loading…" never settled.
 */
let snapshot: PageSnapshot = {
  isLoading: false,
  error: null,
  hasMore: true,
  page: 1,
  chats: [],
};
let started = false;
/** Bumped when a reload or reset supersedes whatever request is in flight. */
let requestSeq = 0;
const listeners = new Set<() => void>();

function chatListContextKey(): string {
  const headers = getWorkflowServiceContextHeaders();
  return `${headers["x-active-org"] ?? ""}\0${headers["x-active-team"] ?? ""}\0${headers["x-teamKey"] ?? ""}`;
}

function isCurrentRequest(seq: number, contextKey: string) {
  return seq === requestSeq && contextKey === chatListContextKey();
}

function emit(next: PageSnapshot) {
  snapshot = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getPageSnapshot() {
  return snapshot;
}

async function loadMoreShared(isReload = false) {
  // A page fetch can wait. An org or team switch cannot: the in-flight
  // response still belongs to the previous workspace.
  if (snapshot.isLoading && !isReload) return;
  if (!isReload && !snapshot.hasMore) return;

  const seq = ++requestSeq;
  const contextKey = chatListContextKey();
  const currentPage = isReload ? 1 : snapshot.page;
  const base = isReload ? [] : snapshot.chats;
  emit({
    ...snapshot,
    isLoading: true,
    error: null,
    page: currentPage,
    hasMore: isReload ? true : snapshot.hasMore,
    chats: base,
  });
  if (isReload) {
    useSessionsStore.getState().updateSessionsStore({
      chats: [],
      isLoadingChats: true,
      chatsError: null,
    });
  }

  try {
    const response = await getChats({ page: currentPage, limit: LIMIT });
    if (!isCurrentRequest(seq, contextKey)) return;
    const newChats = response.threads || [];
    const hasMorePages = newChats.length === LIMIT;
    const combined = [...base, ...newChats];
    const unique = combined.filter(
      (chat, index, self) => index === self.findIndex((c) => c.id === chat.id),
    );
    useSessionsStore.getState().updateSessionsStore({
      chats: unique,
      isLoadingChats: false,
      chatsError: null,
    });
    emit({
      isLoading: false,
      error: null,
      hasMore: hasMorePages,
      page: currentPage + 1,
      chats: unique,
    });
  } catch (err) {
    if (!isCurrentRequest(seq, contextKey)) return;
    const errorMessage =
      err instanceof Error ? err.message : "Failed to load chats";
    useSessionsStore.getState().updateSessionsStore({
      isLoadingChats: false,
      chatsError: errorMessage,
    });
    emit({
      ...snapshot,
      isLoading: false,
      error: errorMessage,
    });
  }
}

function resetShared() {
  requestSeq += 1;
  emit({
    isLoading: false,
    error: null,
    hasMore: true,
    page: 1,
    chats: [],
  });
}

export function useChatsPaginated() {
  const page = useSyncExternalStore(
    subscribe,
    getPageSnapshot,
    getPageSnapshot,
  );

  useEffect(() => {
    if (started) return;
    started = true;
    void loadMoreShared();
  }, []);

  useEffect(() => {
    const handleOrgTeamChange = () => {
      void loadMoreShared(true);
    };
    window.addEventListener(
      ORG_TEAM_SELECTION_CHANGED_EVENT,
      handleOrgTeamChange,
    );
    return () =>
      window.removeEventListener(
        ORG_TEAM_SELECTION_CHANGED_EVENT,
        handleOrgTeamChange,
      );
  }, []);

  return {
    chats: page.chats,
    isLoading: page.isLoading,
    error: page.error,
    hasMore: page.hasMore,
    loadMore: loadMoreShared,
    reset: resetShared,
  };
}
